/**
 * Template Import Modal
 * Import template structure from previous successful proposals
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Modal, Button, Input, Select, Card, Badge } from '@tracekit/core';
import { Search, FileText, Import, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { listReports } from '@tracekit/core';
import type { Report } from '@tracekit/core';
import { useWorkspace } from '@tracekit/core';
import { getAPIClient } from '@tracekit/core';

interface TemplateImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunityId: string;
  projectId: string;
  onTemplateSelected: (template: {
    sections: Array<{ name: string; description: string }>;
    default_questions: Array<{ section: string; question: string }>;
  }) => void;
}

export function TemplateImportModal({
  isOpen,
  onClose,
  opportunityId,
  projectId,
  onTemplateSelected,
}: TemplateImportModalProps) {
  const { projectId: workspaceProjectId } = useWorkspace();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [adaptQuestions, setAdaptQuestions] = useState(true);

  // Fetch reports for selection (filter for RFP proposals)
  const { data: reports, isLoading: reportsLoading } = useQuery({
    queryKey: ['reports', projectId || workspaceProjectId],
    queryFn: () => listReports(projectId || workspaceProjectId || ''),
    enabled: isOpen && !!(projectId || workspaceProjectId),
  });

  // Filter reports to show only RFP proposals
  const filteredReports = useMemo(() => {
    if (!reports) return [];
    
    return reports.filter((r: Report) => {
      // Filter for RFP proposals
      const metadata = (r as any).metadata || (r as any).meta || {};
      const isRfpProposal =
        r.report_type === 'rfp_proposal' ||
        (r.report_type === 'custom' &&
         (metadata.report_type_override === 'rfp_proposal' || metadata.rfp_artifact_id));
      
      if (!isRfpProposal) return false;
      
      // Apply search filter
      if (searchQuery) {
        const reportName = r.name || r.client_name || '';
        return reportName.toLowerCase().includes(searchQuery.toLowerCase());
      }
      
      return true;
    });
  }, [reports, searchQuery]);

  // Fetch template from selected report
  const { data: templateData, isLoading: templateLoading, error: templateError, refetch: refetchTemplate } = useQuery({
    queryKey: ['report-template', selectedReportId, projectId],
    queryFn: async () => {
      if (!selectedReportId || !projectId) {
        throw new Error('Report ID and Project ID are required');
      }
      const api = getAPIClient();
      try {
        console.log(`[TemplateImportModal] Fetching template for report ${selectedReportId} in project ${projectId}`);
        // getAPIClient().get() already returns response.data, not the full response object
        const data = await api.get(`/api/opportunities/reports/${selectedReportId}/template`, {
          params: { project_id: projectId },
        });
        console.log(`[TemplateImportModal] Template loaded:`, data);
        
        // Validate response structure
        if (!data) {
          throw new Error('Empty response from server');
        }
        
        if (data.sections === undefined && data.default_questions === undefined) {
          console.error('[TemplateImportModal] Invalid response structure:', data);
          throw new Error('Invalid response structure from server - missing sections and default_questions');
        }
        
        return data;
      } catch (error: any) {
        console.error('[TemplateImportModal] Failed to fetch template:', error);
        console.error('[TemplateImportModal] Error details:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
          url: error.config?.url,
        });
        throw new Error(error.response?.data?.detail || error.message || 'Failed to load template');
      }
    },
    enabled: isOpen && !!selectedReportId && !!projectId,
    retry: 1,
    staleTime: 0, // Always fetch fresh data
  });

  // Reset template data when report selection changes
  useEffect(() => {
    if (selectedReportId && projectId && isOpen) {
      console.log(`[TemplateImportModal] Report changed to ${selectedReportId}, query should auto-refetch`);
      // Query will automatically refetch when selectedReportId changes (it's in the queryKey)
    }
  }, [selectedReportId, projectId, isOpen]);

  // Import template mutation
  const importMutation = useMutation({
    mutationFn: async () => {
      const api = getAPIClient();
      // getAPIClient().post() already returns response.data, not the full response object
      const data = await api.post(
        `/api/opportunities/${opportunityId}/proposal-template/from-report?project_id=${projectId}`,
        {
          source_report_id: selectedReportId,
          adapt_questions: adaptQuestions,
        }
      );
      console.log('[TemplateImportModal] Import response:', data);
      return data;
    },
    onSuccess: (data) => {
      console.log('[TemplateImportModal] Import success, data:', data);
      if (!data) {
        console.error('[TemplateImportModal] No data in import response');
        alert('Import failed: No data received from server');
        return;
      }
      onTemplateSelected({
        sections: data.sections || [],
        default_questions: data.default_questions || [],
      });
      onClose();
    },
    onError: (error: any) => {
      console.error('Template import failed:', error);
      console.error('Template import error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      alert(`Import failed: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    },
  });

  const handleImport = () => {
    if (!selectedReportId) {
      alert('Please select a report to import from');
      return;
    }

    importMutation.mutate();
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedReportId('');
    setAdaptQuestions(true);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Template from Previous Report"
      size="xl"
    >
      <div className="space-y-4">
        {/* Step 1: Select Source Report */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Step 1: Select Source Report
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search reports by name or client..."
              className="pl-10"
            />
          </div>
          {reportsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <Select
              value={selectedReportId}
              onChange={(e) => {
                const newReportId = e.target.value;
                console.log(`[TemplateImportModal] Report selected: ${newReportId}`);
                setSelectedReportId(newReportId);
              }}
              options={filteredReports.map((r: Report) => ({
                value: r.id,
                label: `${r.name || r.client_name} - ${r.status}`,
              }))}
              placeholder="Select a report to import template from..."
            />
          )}
        </div>

        {/* Step 2: Preview Template */}
        {selectedReportId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Step 2: Preview Template Structure
            </label>
            {templateLoading ? (
              <Card className="p-4">
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm text-gray-600">Loading template...</span>
                </div>
              </Card>
            ) : templateError ? (
              <Card className="p-4">
                <div className="text-center py-4">
                  <AlertCircle className="h-6 w-6 text-red-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-theme-text-secondary mb-2">
                    Failed to load template
                  </p>
                  <p className="text-xs text-gray-500">
                    {templateError instanceof Error ? templateError.message : 'Unknown error occurred'}
                  </p>
                </div>
              </Card>
            ) : templateData ? (
              <Card className="p-4">
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-theme-text mb-2">
                      Sections ({templateData.sections?.length || 0})
                    </p>
                    <div className="space-y-1">
                      {templateData.sections && templateData.sections.length > 0 ? (
                        templateData.sections.map((section: any, idx: number) => (
                          <div key={idx} className="text-sm text-gray-600 dark:text-theme-text-secondary py-1">
                            • {section.name}
                            {section.description && section.description !== section.name && (
                              <span className="text-xs text-gray-500 ml-2">- {section.description}</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-gray-500">No sections found</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-theme-text mb-2">
                      Questions ({templateData.default_questions?.length || 0})
                    </p>
                    <div className="space-y-2">
                      {templateData.default_questions && templateData.default_questions.length > 0 ? (
                        templateData.default_questions.map((q: any, idx: number) => (
                          <div key={idx} className="text-sm text-gray-600 dark:text-theme-text-secondary border-l-2 border-blue-200 dark:border-blue-800 pl-3 py-1">
                            <div className="font-medium text-blue-700 dark:text-blue-400 mb-1">
                              {q.section}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-theme-text-secondary">
                              {q.question}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-gray-500">No questions found</div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ) : null}
          </div>
        )}

        {/* Step 3: Import Options */}
        {selectedReportId && (
          <div className="space-y-3 border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Step 3: Import Options
            </label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={adaptQuestions}
                onChange={(e) => setAdaptQuestions(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 bg-white dark:bg-gray-800"
              />
              <label className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                Adapt questions to new opportunity (recommended)
              </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              When enabled, questions will be automatically adjusted to reference the new opportunity's requirements.
            </p>
          </div>
        )}


        {/* No Reports Found */}
        {!reportsLoading && filteredReports.length === 0 && (
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-theme-text-secondary">
              {searchQuery ? 'No reports found matching your search' : 'No previous proposals found'}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Only RFP proposals are shown for template import
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={importMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!selectedReportId || importMutation.isPending || templateLoading}
            isLoading={importMutation.isPending}
          >
            <Import className="h-4 w-4 mr-2" />
            Import Template
          </Button>
        </div>
      </div>
    </Modal>
  );
}
